/**
 * A pupil's exam timetable, for the pupil or for a parent.
 *
 * Nothing here decides anything: which papers and in what order is
 * lib/examTimetable.ts. This only fetches the three inputs — the published
 * series, the Cambridge timetable file, and the pupil's class and subjects.
 *
 * The pupil comes from `getMyTimetableContext`, which resolves them
 * SERVER-SIDE (a student speaks for their own account, a parent only for a
 * child on theirs) — the same callable the timetable screen uses. A student
 * session subscribes to nothing, so the subjects cannot come from anywhere
 * else. The callable is skipped when no published series includes the
 * pupil's class, so a Form 2 pupil's portal costs one small query.
 */
import { useEffect, useState } from 'react';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import {
  compareSittings,
  levelForClass,
  sittingsFor,
  type CambridgeTimetable,
  type ExamSeries,
  type PupilExamEntries,
  type Sitting,
} from '@/lib/examTimetable';

/** Every timetable file the portal can read, lazily: ~90 KB each. */
const TIMETABLES: Record<string, () => Promise<CambridgeTimetable>> = {
  'cambridge-2026-11-zone3': () =>
    import('@/content/examTimetables/cambridge-2026-11-zone3.json').then((m) => m.default as CambridgeTimetable),
};

export const TIMETABLE_IDS = Object.keys(TIMETABLES);

/** The stand-in "timetable" for an internal series. */
export const EMPTY_TIMETABLE: CambridgeTimetable = { id: '', series: '', zone: 0, source: '', extracted: '', components: [] };

export function loadTimetable(id: string): Promise<CambridgeTimetable | null> {
  const load = TIMETABLES[id];
  return load ? load() : Promise.resolve(null);
}

const loadPupil = httpsCallable<{ studentId?: string }, { className: string; subjectIds: string[] }>(
  functions, 'getMyTimetableContext',
);

export interface SeriesSitting extends Sitting {
  seriesId: string;
  countdownFrom: string;
}

export interface ExamTimetableState {
  loading: boolean;
  error: string | null;
  /** The published series this pupil sits. */
  series: ExamSeries[];
  sittings: SeriesSitting[];
  /** From the statement of entry, where one has been imported. It is on the
   *  pupil's printed timetable, because it is what an invigilator asks for
   *  and what goes on the desk. */
  candidateNumber: string;
}

export function useExamTimetable(opts: {
  /** A parent's child. Omit for a student's own account. */
  studentId?: string;
  /** Known up front for a student (their account carries it), so a pupil in
   *  no exam class never calls the function at all. */
  className?: string;
  /** The pupil whose statement of entry to read — the student's own id, or the
   *  parent's child. Without it the subject defaults apply. */
  pupilId?: string;
  enabled?: boolean;
}): ExamTimetableState {
  const { studentId, className, pupilId, enabled = true } = opts;
  const [state, setState] = useState<ExamTimetableState>({ loading: true, error: null, series: [], sittings: [], candidateNumber: '' });

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    (async () => {
      const snap = await getDocs(query(collection(db, 'reportExamSeries'), where('published', '==', true)));
      let published = snap.docs.map((d) => ({ ...(d.data() as ExamSeries), id: d.id }));
      if (className) published = published.filter((s) => levelForClass(s, className));
      if (!published.length) return { series: [], sittings: [], candidateNumber: '' };

      const pupil = (await loadPupil(studentId ? { studentId } : {})).data;
      const mine = published.filter((s) => levelForClass(s, pupil.className));
      const sittings: SeriesSitting[] = [];
      let candidateNumber = '';
      for (const s of mine) {
        // An internal series has no Cambridge file: its papers are on the entries.
        const timetable = s.kind === 'internal' ? EMPTY_TIMETABLE : await loadTimetable(s.timetableId);
        if (!timetable) continue;
        // The pupil's real entries, when the statement has been imported.
        let entries: PupilExamEntries | null = null;
        if (pupilId) {
          try {
            const snap = await getDoc(doc(db, 'reportExamEntries', `${s.id}_${pupilId}`));
            entries = snap.exists() ? (snap.data() as PupilExamEntries) : null;
            if (entries?.candidateNumber) candidateNumber = entries.candidateNumber;
          } catch { entries = null; }
        }
        if (s.kind === 'internal' && !entries) continue;
        for (const x of sittingsFor(s, timetable, pupil, entries?.components)) {
          const sessionLabel = s.sessionLabels?.[x.session];
          sittings.push({ ...x, seriesId: s.id, countdownFrom: s.countdownFrom, ...(sessionLabel ? { sessionLabel } : {}) });
        }
      }
      sittings.sort(compareSittings);
      return { series: mine, sittings, candidateNumber };
    })()
      .then((r) => { if (!cancelled) setState({ loading: false, error: null, ...r }); })
      .catch((e: unknown) => {
        if (cancelled) return;
        console.error('exam timetable', e);
        setState({ loading: false, error: 'The exam timetable could not be loaded.', series: [], sittings: [], candidateNumber: '' });
      });
    return () => { cancelled = true; };
  }, [studentId, className, pupilId, enabled]);

  return state;
}

/** The clock the countdowns read, ticking once a minute. */
export function useMinuteClock(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(t);
  }, []);
  return now;
}
